/**
 * Monoline UI — murakkab komponentlar (composites).
 * DESIGN.md 10-bo'limdagi shartnoma. Tashqi bog'liqlik yo'q:
 * faqat react, react-dom (portal) va react-icons/hi2.
 * Barcha sinflar `ui-` prefiksi bilan (9-bo'lim), qattiq kodlangan rang yo'q.
 */

import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import {
  HiArrowSmallDown,
  HiArrowSmallUp,
  HiCheckCircle,
  HiChevronDown,
  HiChevronLeft,
  HiChevronRight,
  HiChevronUp,
  HiEllipsisHorizontal,
  HiInbox,
  HiInformationCircle,
  HiMagnifyingGlass,
  HiSignalSlash,
  HiXCircle,
  HiXMark,
} from 'react-icons/hi2'

import { Button, Field, Input, Kbd, Skeleton } from './primitives'
import { Beacon } from './loaders.jsx'

/* ─────────────────────────────────────────────────────────────
   Ichki yordamchilar (eksport qilinmaydi)
   ───────────────────────────────────────────────────────────── */

/** Sinf nomlarini birlashtiradi, bo'sh qiymatlarni tashlab ketadi. */
function cx(...parts) {
  return parts.filter(Boolean).join(' ')
}

/** Modal ichida fokus ushlab turish uchun fokuslanadigan elementlar selektori. */
const FOCUS_SEL = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusablesIn(node) {
  if (!node) return []
  return Array.from(node.querySelectorAll(FOCUS_SEL)).filter(
    (el) => !el.hasAttribute('disabled') && el.getClientRects().length > 0
  )
}

/* Body skrollini bloklash — bir nechta ochiq oyna uchun sanoq bilan. */
let scrollLocks = 0
let savedOverflow = ''

function lockBodyScroll() {
  if (typeof document === 'undefined') return
  if (scrollLocks === 0) {
    savedOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  scrollLocks += 1
}

function unlockBodyScroll() {
  if (typeof document === 'undefined') return
  scrollLocks = Math.max(0, scrollLocks - 1)
  if (scrollLocks === 0) document.body.style.overflow = savedOverflow
}

/* Ochiq oynalar steki — Esc faqat eng ustki oynani yopadi. */
const dialogStack = []

/**
 * Modal/Drawer/CommandPalette uchun umumiy xulq:
 * Esc yopadi, fokus panel ichida qoladi, body skroll bloklanadi,
 * yopilganda fokus oynani ochgan elementga qaytadi.
 */
function useDialog(open, onClose, panelRef) {
  const closeRef = useRef(onClose)
  useEffect(() => {
    closeRef.current = onClose
  })

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined

    const token = {}
    dialogStack.push(token)
    const opener = document.activeElement
    lockBodyScroll()

    const raf = requestAnimationFrame(() => {
      const list = focusablesIn(panelRef.current)
      const target = list[0] || panelRef.current
      if (target && typeof target.focus === 'function') target.focus()
    })

    const onKeyDown = (e) => {
      // faqat eng ustki oyna javob beradi
      if (dialogStack[dialogStack.length - 1] !== token) return

      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        closeRef.current?.()
        return
      }
      if (e.key !== 'Tab') return

      const panel = panelRef.current
      const list = focusablesIn(panel)
      if (!list.length) {
        e.preventDefault()
        panel?.focus?.()
        return
      }
      const first = list[0]
      const last = list[list.length - 1]
      const active = document.activeElement

      if (!panel || !panel.contains(active)) {
        e.preventDefault()
        ;(e.shiftKey ? last : first).focus()
        return
      }
      if (e.shiftKey && active === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('keydown', onKeyDown, true)
      const i = dialogStack.indexOf(token)
      if (i > -1) dialogStack.splice(i, 1)
      unlockBodyScroll()
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus()
      }
    }
  }, [open, panelRef])
}

/** Portalni faqat brauzerda ochadi. */
function Portal({ children }) {
  if (typeof document === 'undefined') return null
  return createPortal(children, document.body)
}

/* ─────────────────────────────────────────────────────────────
   Card
   ───────────────────────────────────────────────────────────── */

/** Karta: 1px chegara, soyasiz. Sarlavha, o'ng tarafdagi amallar va futer ixtiyoriy. */
export const Card = forwardRef(function Card(
  { title, actions, icon: Icon, footer, className, children, ...rest },
  ref
) {
  const hasHdr = Boolean(title || actions || Icon)
  return (
    <section ref={ref} className={cx('ui-card', className)} {...rest}>
      {hasHdr && (
        <header className="ui-card__hdr">
          <div className="ui-card__title">
            {Icon && <Icon aria-hidden="true" />}
            {title}
          </div>
          {actions && <div className="ui-card__actions">{actions}</div>}
        </header>
      )}
      <div className="ui-card__body">{children}</div>
      {footer && <footer className="ui-card__ftr">{footer}</footer>}
    </section>
  )
})

/* ─────────────────────────────────────────────────────────────
   PageHeader / Toolbar
   ───────────────────────────────────────────────────────────── */

/** Sahifa sarlavhasi: chapda nom va izoh, o'ngda amallar. */
export const PageHeader = forwardRef(function PageHeader(
  { title, sub, actions, className, children, ...rest },
  ref
) {
  return (
    <div ref={ref} className={cx('ui-page-hdr', className)} {...rest}>
      <div>
        <h2 className="ui-page-hdr__title">{title}</h2>
        {sub && <div className="ui-page-hdr__sub">{sub}</div>}
      </div>
      {(actions || children) && (
        <div className="ui-row ui-gap-8">
          {actions}
          {children}
        </div>
      )}
    </div>
  )
})

/** Filtrlar va amallar uchun gorizontal panel. */
export const Toolbar = forwardRef(function Toolbar({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={cx('ui-toolbar', className)} {...rest}>
      {children}
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   EmptyState (Table undan foydalanadi, shuning uchun yuqorida)
   ───────────────────────────────────────────────────────────── */

/** Bo'sh holat: ikonka, sarlavha, izoh va bitta amal. */
export const EmptyState = forwardRef(function EmptyState(
  { title = "Ma'lumot yo'q", text, icon: Icon = HiInbox, action, className, ...rest },
  ref
) {
  return (
    <div ref={ref} className={cx('ui-empty', className)} {...rest}>
      {Icon && <Icon aria-hidden="true" />}
      <div className="ui-empty__title">{title}</div>
      {text && <div className="ui-empty__text">{text}</div>}
      {action && <div className="ui-empty__action">{action}</div>}
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   ConnectionLost — server javob bermayotgan holat
   ───────────────────────────────────────────────────────────── */

/**
 * Aloqa uzilgan holat.
 *
 * Nega `EmptyState` yetmaydi: bo'sh ro'yxat va javob bermayotgan server —
 * ikki xil xabar. Birinchisida ma'lumot YO'Q, ikkinchisida ma'lumot BOR,
 * lekin unga yetib bo'lmayapti. Operator uchun farqi katta: birinchisida ish
 * davom etadi, ikkinchisida kutish yoki administratorga aytish kerak.
 * Shuning uchun matn ham, taklif qilinadigan amal ham boshqa.
 *
 * Komponent sanoqni o'zi yuritadi: `retryIn` soniyadan keyin `onRetry` ni
 * chaqiradi va sanoqni qaytadan boshlaydi — operator sahifani qo'lda
 * yangilab o'tirmaydi, server ko'tarilishi bilan ekran o'zi tiklanadi.
 * `busy` paytida sanoq to'xtaydi, aks holda tugamagan urinish ustiga
 * yangisi tushadi.
 *
 * @param onRetry  qayta urinish — tugma ham, avtomatik sanoq ham shuni chaqiradi
 * @param retryIn  avtomatik urinishgacha soniya. 0 yoki berilmasa — avtomatik yo'q
 * @param busy     urinish ketmoqda: tugma bloklanadi, sanoq to'xtaydi
 * @param detail   texnik tafsilot — operator uni administratorga ko'chirib beradi
 */
export const ConnectionLost = forwardRef(function ConnectionLost(
  {
    title = "Server bilan aloqa yo'q",
    text = "Ma'lumotni olib bo'lmadi. Internet aloqangizni tekshiring — muammo saqlansa, server vaqtincha o'chgan bo'lishi mumkin.",
    onRetry,
    retryIn = 0,
    busy = false,
    detail,
    icon: Icon = HiSignalSlash,
    className,
    ...rest
  },
  ref
) {
  const auto = Boolean(onRetry) && retryIn > 0
  const [left, setLeft] = useState(retryIn)

  // Sanoqni boshidan qo'yamiz: parametr o'zgarganda va har urinish tugaganda.
  useEffect(() => { setLeft(retryIn) }, [retryIn, busy])

  useEffect(() => {
    if (!auto || busy) return undefined
    const id = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [auto, busy])

  // Nolga yetdi — urinamiz. Sanoq shu yerda qayta tiklanadi, shuning uchun
  // effekt ikkinchi marta ishga tushmaydi (StrictMode'da ham).
  useEffect(() => {
    if (!auto || busy || left > 0) return
    setLeft(retryIn)
    onRetry()
  }, [auto, busy, left, retryIn, onRetry])

  return (
    <div
      ref={ref}
      className={cx('ui-conn', className)}
      role="status"
      aria-live="polite"
      {...rest}
    >
      <EmptyState
        icon={Icon}
        title={title}
        text={text}
        action={onRetry && (
          <div className="ui-conn__act">
            <Button variant="primary" size="sm" onClick={onRetry} disabled={busy}>
              {busy ? 'Ulanmoqda…' : 'Qayta urinish'}
            </Button>
            {auto && !busy && (
              <span className="ui-conn__timer">
                {left} soniyadan keyin avtomatik
              </span>
            )}
          </div>
        )}
      />
      {detail && <div className="ui-conn__detail ui-mono">{detail}</div>}
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   Freshness — raqam qachonligi
   ───────────────────────────────────────────────────────────── */

/**
 * Oxirgi yangilanish belgisi.
 *
 * Kuzatuv ekranida raqamning o'zi yetmaydi — u qachonligi ham ma'lumot.
 * Qo'lda bosiladigan "Yangilash" tugmasi bu savolga javob bermaydi: ekran
 * eskirganini o'zi aytmaydi. Shuning uchun sahifa avtomatik yangilanadi,
 * bu belgi esa qachon yangilangani va kechikkani (yoki kechikmagani) ni aytadi.
 *
 * Vaqt `HH:MM` ko'rinishida qo'lda yig'iladi — `toLocaleTimeString` ba'zi
 * muhitlarda o'zbek lokalida chala natija beradi (DESIGN.md 12).
 *
 * @param at          oxirgi muvaffaqiyatli yuklanish vaqti (Date | ISO | ms)
 * @param staleAfter  necha soniyadan keyin "eskirgan" hisoblanadi (standart 60)
 * @param label       oldingi so'z (standart "Yangilandi")
 */
export const Freshness = forwardRef(function Freshness(
  { at, staleAfter = 60, label = 'Yangilandi', className, ...rest },
  ref
) {
  const ts = useMemo(() => {
    if (at == null) return null
    const t = at instanceof Date ? at.getTime() : new Date(at).getTime()
    return Number.isNaN(t) ? null : t
  }, [at])

  const [now, setNow] = useState(() => Date.now())

  // 10s qadam yetarli: belgi soniyalarni ko'rsatmaydi, faqat eskirganini aytadi
  useEffect(() => {
    if (ts == null) return undefined
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 10000)
    return () => clearInterval(id)
  }, [ts])

  if (ts == null) return null

  const stale = (now - ts) / 1000 > staleAfter
  const d = new Date(ts)
  const clock = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`

  return (
    <span
      ref={ref}
      className={cx('ui-freshness', stale && 'is-stale', className)}
      role="status"
      aria-live="off"
      {...rest}
    >
      <span className="ui-freshness__dot" aria-hidden="true" />
      {stale ? `${clock} dan beri yangilanmadi` : `${label} ${clock}`}
    </span>
  )
})

/* ─────────────────────────────────────────────────────────────
   Table
   ───────────────────────────────────────────────────────────── */

/** Keyingi saralash yo'nalishi: asc → desc → asc. */
function nextSortDir(sort, key) {
  if (!sort || sort.key !== key) return 'asc'
  return sort.dir === 'asc' ? 'desc' : 'asc'
}

/**
 * Jadval. Saralashni O'ZI qilmaydi — faqat `onSort(key, dir)` ni chaqiradi.
 * `columns`: {key,title,width,align,sortable,render,className}
 *   `className` ustunning th va td kataklariga beriladi — masalan amallar
 *   ustuni uchun `ui-table__actions-col` (kit.surfaces.css).
 * `sort`: {key, dir:'asc'|'desc'}
 */
export const Table = forwardRef(function Table(
  {
    columns = [],
    rows = [],
    loading = false,
    loadingRows = 5,
    empty,
    onRowClick,
    sort,
    onSort,
    dense = false,
    stickyHeader = false,
    rowKey,
    keepHeaderWhenEmpty = false,
    className,
    ...rest
  },
  ref
) {
  const clickable = typeof onRowClick === 'function'

  // Ustun sarlavhalari bo'sh holat USTIDA osilib qolmaydi.
  // Sabab: "TICKET № · MUHIMLIK · OPERATOR" qatori jadval BOR degan va'da
  // beradi — ko'z ustunlarni o'qiydi, pastda esa hech narsa yo'q. Bo'sh
  // holat o'zi to'liq xabar: sarlavha unga hech narsa qo'shmaydi.
  // Ustunlar tarkibi muhim bo'lgan kamdan-kam joyda `keepHeaderWhenEmpty`.
  const isEmpty = !loading && rows.length === 0
  const showHead = !isEmpty || keepHeaderWhenEmpty

  const handleRowKeyDown = (e, row, i) => {
    if (!clickable) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onRowClick(row, i)
    }
  }

  const emptyNode = useMemo(() => {
    if (empty === undefined || empty === null) return <EmptyState />
    if (typeof empty === 'string') return <EmptyState title={empty} />
    return empty
  }, [empty])

  return (
    <div
      ref={ref}
      className={cx('ui-table-wrap', stickyHeader && 'is-sticky', className)}
      {...rest}
    >
      <table className={cx('ui-table', dense && 'ui-table--dense')}>
        {showHead && (
        <thead>
          <tr>
            {columns.map((col) => {
              const sorted = sort && sort.key === col.key ? sort.dir : null
              const canSort = Boolean(col.sortable && onSort)
              return (
                <th
                  key={col.key}
                  scope="col"
                  style={{
                    width: col.width || undefined,
                    textAlign: col.align || undefined,
                  }}
                  className={cx(
                    col.className,
                    canSort && 'is-sortable',
                    sorted === 'asc' && 'is-sorted-asc',
                    sorted === 'desc' && 'is-sorted-desc'
                  )}
                  aria-sort={
                    sorted === 'asc'
                      ? 'ascending'
                      : sorted === 'desc'
                        ? 'descending'
                        : canSort
                          ? 'none'
                          : undefined
                  }
                >
                  {canSort ? (
                    <button
                      type="button"
                      className="ui-btn ui-btn--ghost ui-btn--sm"
                      onClick={() => onSort(col.key, nextSortDir(sort, col.key))}
                    >
                      {col.title}
                      {sorted === 'asc' && <HiChevronUp aria-hidden="true" />}
                      {sorted === 'desc' && <HiChevronDown aria-hidden="true" />}
                    </button>
                  ) : (
                    col.title
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        )}

        <tbody>
          {loading &&
            Array.from({ length: loadingRows }).map((_, r) => (
              <tr key={`sk-${r}`}>
                {columns.map((col) => (
                  <td key={col.key}>
                    <Skeleton variant="text" />
                  </td>
                ))}
              </tr>
            ))}

          {isEmpty && (
            <tr>
              <td colSpan={Math.max(1, columns.length)}>{emptyNode}</td>
            </tr>
          )}

          {!loading &&
            rows.map((row, i) => (
              <tr
                key={rowKey ? rowKey(row, i) : (row?.id ?? i)}
                className={cx(clickable && 'is-clickable')}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => onRowClick(row, i) : undefined}
                onKeyDown={clickable ? (e) => handleRowKeyDown(e, row, i) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={col.className || undefined}
                    style={{ textAlign: col.align || undefined }}
                  >
                    {col.render ? col.render(row, i) : row?.[col.key]}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   Tabs
   ───────────────────────────────────────────────────────────── */

/** Bo'limlar: `variant="line"` (standart) yoki `"segmented"`. Strelkalar bilan yuriladi. */
export const Tabs = forwardRef(function Tabs(
  { items = [], value, onChange, variant = 'line', className, ...rest },
  ref
) {
  const onKeyDown = (e) => {
    const i = items.findIndex((it) => it.id === value)
    if (i < 0) return
    let next = null
    if (e.key === 'ArrowRight') next = (i + 1) % items.length
    else if (e.key === 'ArrowLeft') next = (i - 1 + items.length) % items.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = items.length - 1
    if (next === null) return
    e.preventDefault()
    onChange?.(items[next].id)
  }

  return (
    <div
      ref={ref}
      role="tablist"
      onKeyDown={onKeyDown}
      className={cx('ui-tabs', variant === 'segmented' && 'ui-tabs--segmented', className)}
      {...rest}
    >
      {items.map((it) => {
        const active = it.id === value
        const Icon = it.icon
        return (
          <button
            key={it.id}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            className={cx('ui-tabs__item', active && 'is-active')}
            onClick={() => onChange?.(it.id)}
          >
            {Icon && <Icon aria-hidden="true" />}
            {it.label}
            {it.count != null && <span className="ui-tabs__count ui-nums">{it.count}</span>}
          </button>
        )
      })}
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   Alert
   ───────────────────────────────────────────────────────────── */

const ALERT_ICON = {
  info: HiInformationCircle,
  // Ogohlantirishda uchburchak emas, mayoq: butun konsolda diqqat belgisi bitta
  warn: Beacon,
  danger: HiXCircle,
  ok: HiCheckCircle,
}

/** Xabar bloki: info / warn / danger / ok. Rang yonida doim matn turadi. */
export const Alert = forwardRef(function Alert(
  { tone = 'info', title, onClose, icon, className, children, ...rest },
  ref
) {
  const Icon = icon || ALERT_ICON[tone] || HiInformationCircle
  return (
    <div
      ref={ref}
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cx('ui-alert', `ui-alert--${tone}`, className)}
      {...rest}
    >
      <span className="ui-alert__icon">
        <Icon aria-hidden="true" />
      </span>
      <div className="ui-alert__body">
        {title && <strong>{title}</strong>}
        {children}
      </div>
      {onClose && (
        <Button
          variant="ghost"
          size="sm"
          icon={HiXMark}
          iconOnly
          aria-label="Xabarni yopish"
          onClick={onClose}
        />
      )}
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   Modal
   ───────────────────────────────────────────────────────────── */

/**
 * Modal oyna: portal orqali `document.body` ga chiqadi.
 * Esc yopadi, fokus ichida qoladi, ochiqda body skroll bloklanadi.
 */
export const Modal = forwardRef(function Modal(
  {
    open,
    onClose,
    title,
    footer,
    size = 'md',
    closeOnOverlay = true,
    busy = false,
    className,
    children,
    ...rest
  },
  ref
) {
  const panelRef = useRef(null)
  const titleId = useId()
  // `busy` — oynada so'rov ketyapti: Esc ham, fon bosish ham yopmaydi,
  // yopish tugmasi joyida qoladi lekin o'chiq. Tugma yo'qolib qolsa
  // sarlavha qatori sakrardi va foydalanuvchi nima bo'lganini tushunmasdi.
  useDialog(open, busy ? undefined : onClose, panelRef)

  if (!open) return null

  return (
    <Portal>
      <div
        className="ui-modal-overlay"
        onMouseDown={(e) => {
          if (!busy && closeOnOverlay && e.target === e.currentTarget) onClose?.()
        }}
      >
        <div
          ref={(node) => {
            panelRef.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) ref.current = node
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className={cx('ui-modal', size !== 'md' && `ui-modal--${size}`, className)}
          {...rest}
        >
          {(title || onClose) && (
            <header className="ui-modal__hdr">
              {/* Sarlavha — haqiqiy heading (a11y), o'lchami CSS da beriladi */}
              <h2 id={titleId} className="ui-modal__title">{title}</h2>
              {onClose && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={HiXMark}
                  iconOnly
                  aria-label="Oynani yopish"
                  onClick={onClose}
                  disabled={busy}
                />
              )}
            </header>
          )}
          <div className="ui-modal__body">{children}</div>
          {footer && <footer className="ui-modal__ftr">{footer}</footer>}
        </div>
      </div>
    </Portal>
  )
})

/* ─────────────────────────────────────────────────────────────
   ConfirmModal
   ───────────────────────────────────────────────────────────── */

/**
 * Tasdiqlash oynasi. `confirmWord` berilsa, foydalanuvchi shu so'zni
 * aynan yozmaguncha tasdiq tugmasi o'chiq turadi.
 */
export const ConfirmModal = forwardRef(function ConfirmModal(
  {
    open,
    onClose,
    onConfirm,
    title = 'Tasdiqlaysizmi?',
    text,
    confirmWord,
    danger = false,
    confirmLabel = 'Tasdiqlash',
    cancelLabel = 'Bekor qilish',
    loading = false,
    className,
    children,
    ...rest
  },
  ref
) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const inputId = useId()

  useEffect(() => {
    if (open) { setTyped(''); setBusy(false) }
  }, [open])

  // Ikki marta bosishdan himoya. `onConfirm` promise qaytarsa, u tugagunicha
  // oyna "band" holatda turadi: tasdiq tugmasi yuklanmoqda, bekor tugmasi
  // o'chiq, Esc va fon bosish yopmaydi. Shu tufayli har bir chaqiruv joyida
  // alohida `loading` holati yozish shart emas — o'chirish so'rovi ikki marta
  // ketmaydi. Tashqaridan berilgan `loading` propi ham hisobga olinadi.
  const pending = busy || loading

  const handleConfirm = async () => {
    if (pending) return
    let result
    try {
      result = onConfirm?.()
    } catch {
      return               // sinxron xato — chaqiruvchi o'zi ko'rsatadi
    }
    if (!result || typeof result.then !== 'function') return
    setBusy(true)
    try { await result } catch { /* xabarni chaqiruvchi chiqaradi */ }
    finally { setBusy(false) }
  }

  const ready = !confirmWord || typed.trim() === String(confirmWord)

  return (
    <Modal
      ref={ref}
      open={open}
      onClose={onClose}
      busy={pending}
      title={title}
      size="sm"
      className={className}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={handleConfirm}
            disabled={!ready || pending}
            loading={pending}
          >
            {confirmLabel}
          </Button>
        </>
      }
      {...rest}
    >
      {text && <p>{text}</p>}
      {children}
      {confirmWord && (
        <Field
          label={`Tasdiqlash uchun "${confirmWord}" so'zini yozing`}
          htmlFor={inputId}
          hint="Bu amalni qaytarib bo'lmaydi."
        >
          <Input
            id={inputId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={String(confirmWord)}
            autoComplete="off"
          />
        </Field>
      )}
    </Modal>
  )
})

/* ─────────────────────────────────────────────────────────────
   Drawer
   ───────────────────────────────────────────────────────────── */

/** Yon panel: o'ngdan yoki pastdan chiqadi. Modal bilan bir xil xulq. */
export const Drawer = forwardRef(function Drawer(
  { open, onClose, side = 'right', title, footer, closeOnOverlay = true, className, children, ...rest },
  ref
) {
  const panelRef = useRef(null)
  const titleId = useId()
  useDialog(open, onClose, panelRef)

  if (!open) return null

  return (
    <Portal>
      <div
        className="ui-modal-overlay"
        onMouseDown={(e) => {
          if (closeOnOverlay && e.target === e.currentTarget) onClose?.()
        }}
      >
        <div
          ref={(node) => {
            panelRef.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) ref.current = node
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className={cx('ui-drawer', `ui-drawer--${side}`, className)}
          {...rest}
        >
          {(title || onClose) && (
            <header className="ui-modal__hdr">
              {/* Modal bilan bir xil sarlavha semantikasi va sinfi */}
              <h2 id={titleId} className="ui-modal__title ui-drawer__title">{title}</h2>
              {onClose && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={HiXMark}
                  iconOnly
                  aria-label="Panelni yopish"
                  onClick={onClose}
                />
              )}
            </header>
          )}
          <div className="ui-modal__body">{children}</div>
          {footer && <footer className="ui-modal__ftr">{footer}</footer>}
        </div>
      </div>
    </Portal>
  )
})

/* ─────────────────────────────────────────────────────────────
   Menu
   ───────────────────────────────────────────────────────────── */

/**
 * Ochiluvchi menyu. `trigger` — tugma tugunni (element bo'lsa unga onClick ulanadi).
 * `items`: {label,icon,onClick,danger,sep}. Strelkalar bilan yuriladi, Esc yopadi.
 */
export const Menu = forwardRef(function Menu(
  { trigger, items = [], align = 'start', className, ...rest },
  ref
) {
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(-1)
  const wrapRef = useRef(null)
  const menuRef = useRef(null)

  const actionable = items.filter((it) => !it.sep)

  useEffect(() => {
    if (!open) return undefined
    const onDocDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [open])

  useEffect(() => {
    if (open) setCursor(-1)
  }, [open])

  const onKeyDown = (e) => {
    // Esc faqat menyu ochiq bo'lganda ushlanadi — yopiq bo'lsa hodisa
    // yuqoriga (masalan modalga) o'tib ketishi kerak
    if (e.key === 'Escape') {
      if (!open) return
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
      return
    }
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault()
        setOpen(true)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => (c + 1) % Math.max(1, actionable.length))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => (c - 1 + actionable.length) % Math.max(1, actionable.length))
    } else if (e.key === 'Enter' && cursor >= 0) {
      e.preventDefault()
      const it = actionable[cursor]
      setOpen(false)
      it?.onClick?.()
    }
  }

  const toggleOpen = () => setOpen((v) => !v)

  // berilgan tugma tuguniga onClick va aria biriktiriladi, aks holda standart ikonka-tugma
  const triggerNode = isValidElement(trigger) ? (
    cloneElement(trigger, {
      'aria-haspopup': 'menu',
      'aria-expanded': open,
      onClick: (e) => {
        trigger.props.onClick?.(e)
        toggleOpen()
      },
    })
  ) : (
    <Button
      variant="ghost"
      size="sm"
      icon={HiEllipsisHorizontal}
      iconOnly
      aria-label={typeof trigger === 'string' ? trigger : 'Menyu'}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={toggleOpen}
    />
  )

  return (
    <div
      ref={(node) => {
        wrapRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      }}
      className={cx('ui-menu-wrap', className)}
      onKeyDown={onKeyDown}
      {...rest}
    >
      {triggerNode}
      {open && (
        <div ref={menuRef} role="menu" className={cx('ui-menu', `ui-menu--${align}`)}>
          {items.map((it, i) => {
            if (it.sep) return <div key={`sep-${i}`} className="ui-menu__sep" role="separator" />
            const idx = actionable.indexOf(it)
            const Icon = it.icon
            return (
              <button
                key={it.label ?? i}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                className={cx(
                  'ui-menu__item',
                  it.danger && 'ui-menu__item--danger',
                  idx === cursor && 'is-active'
                )}
                onMouseEnter={() => setCursor(idx)}
                onClick={() => {
                  setOpen(false)
                  it.onClick?.()
                }}
              >
                {Icon && <Icon aria-hidden="true" />}
                {it.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   Toast
   ───────────────────────────────────────────────────────────── */

const ToastCtx = createContext(null)

const TOAST_ICON = {
  info: HiInformationCircle,
  ok: HiCheckCircle,
  critical: HiXCircle,
}

/**
 * Toast provayderi. Ichida `useToast()` → { push(text, tone, duration), dismiss(id) }.
 * Standart yashash muddati — 5 soniya, qo'lda ham yopiladi.
 */
export function ToastProvider({ children, duration = 5000, max = 4 }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef(new Map())
  const seq = useRef(0)

  const dismiss = useCallback((id) => {
    const t = timers.current.get(id)
    if (t) {
      clearTimeout(t)
      timers.current.delete(id)
    }
    setToasts((list) => list.filter((x) => x.id !== id))
  }, [])

  const push = useCallback(
    (text, tone = 'info', ms) => {
      seq.current += 1
      const id = seq.current
      const life = typeof ms === 'number' ? ms : duration
      setToasts((list) => [...list, { id, text, tone }].slice(-max))
      if (life > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), life)
        )
      }
      return id
    },
    [duration, max, dismiss]
  )

  // komponent o'chirilganda barcha taymerlar tozalanadi
  useEffect(() => {
    const store = timers.current
    return () => {
      store.forEach((t) => clearTimeout(t))
      store.clear()
    }
  }, [])

  const value = useMemo(() => ({ push, dismiss, toasts }), [push, dismiss, toasts])

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <Portal>
        <div className="ui-toast-region">
          {toasts.map((t) => {
            const Icon = TOAST_ICON[t.tone] || HiInformationCircle
            return (
              <div
                key={t.id}
                className={cx('ui-toast', t.tone && `ui-toast--${t.tone}`)}
                role={t.tone === 'critical' ? 'alert' : 'status'}
                aria-live={t.tone === 'critical' ? 'assertive' : 'polite'}
              >
                <Icon aria-hidden="true" />
                <span className="ui-grow">{t.text}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={HiXMark}
                  iconOnly
                  aria-label="Bildirishnomani yopish"
                  onClick={() => dismiss(t.id)}
                />
              </div>
            )
          })}
        </div>
      </Portal>
    </ToastCtx.Provider>
  )
}

/** Toast yuborish uchun hook. Provayder bo'lmasa tushunarli xato beradi. */
export function useToast() {
  const ctx = useContext(ToastCtx)
  if (!ctx) {
    throw new Error(
      "useToast() faqat <ToastProvider> ichida ishlaydi. Ilova ildizini <ToastProvider> bilan o'rang."
    )
  }
  return ctx
}

/* ─────────────────────────────────────────────────────────────
   Pagination
   ───────────────────────────────────────────────────────────── */

/** Sahifa raqamlari ro'yxati, ko'p bo'lsa orasiga uch nuqta qo'yiladi. */
function pageWindow(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1)
  const out = [1]
  const from = Math.max(2, page - 1)
  const to = Math.min(pages - 1, page + 1)
  if (from > 2) out.push('…')
  for (let p = from; p <= to; p += 1) out.push(p)
  if (to < pages - 1) out.push('…')
  out.push(pages)
  return out
}

/** Sahifalash: `page` 1 dan boshlanadi, `total` — umumiy yozuvlar soni. */
export const Pagination = forwardRef(function Pagination(
  { page = 1, total = 0, perPage = 20, onChange, className, ...rest },
  ref
) {
  const pages = Math.max(1, Math.ceil(total / Math.max(1, perPage)))
  const cur = Math.min(Math.max(1, page), pages)
  const from = total === 0 ? 0 : (cur - 1) * perPage + 1
  const to = Math.min(total, cur * perPage)

  const go = (p) => {
    if (p < 1 || p > pages || p === cur) return
    onChange?.(p)
  }

  return (
    <nav ref={ref} aria-label="Sahifalash" className={cx('ui-pagination', className)} {...rest}>
      <span className="ui-pagination__info ui-nums">
        {from}–{to} / {total}
      </span>
      <button
        type="button"
        className="ui-pagination__btn"
        aria-label="Oldingi sahifa"
        disabled={cur <= 1}
        onClick={() => go(cur - 1)}
      >
        <HiChevronLeft aria-hidden="true" />
      </button>
      {pageWindow(cur, pages).map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className="ui-pagination__btn is-disabled" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className={cx('ui-pagination__btn', 'ui-nums', p === cur && 'is-active')}
            aria-current={p === cur ? 'page' : undefined}
            aria-label={`${p}-sahifa`}
            onClick={() => go(p)}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        className="ui-pagination__btn"
        aria-label="Keyingi sahifa"
        disabled={cur >= pages}
        onClick={() => go(cur + 1)}
      >
        <HiChevronRight aria-hidden="true" />
      </button>
    </nav>
  )
})

/* ─────────────────────────────────────────────────────────────
   Breadcrumb
   ───────────────────────────────────────────────────────────── */

/** Yo'l ko'rsatkichi: oxirgi element havola emas, `aria-current="page"`. */
export const Breadcrumb = forwardRef(function Breadcrumb({ items = [], className, ...rest }, ref) {
  return (
    <nav ref={ref} aria-label="Yo'l" className={cx('ui-breadcrumb', className)} {...rest}>
      {items.map((it, i) => {
        const last = i === items.length - 1
        return (
          <span key={`${it.label}-${i}`}>
            {i > 0 && (
              <span className="ui-breadcrumb__sep" aria-hidden="true">
                <HiChevronRight />
              </span>
            )}
            {last || !it.href ? (
              <span aria-current={last ? 'page' : undefined}>{it.label}</span>
            ) : (
              <a className="ui-link" href={it.href}>
                {it.label}
              </a>
            )}
          </span>
        )
      })}
    </nav>
  )
})

/* ─────────────────────────────────────────────────────────────
   Sparkline
   ───────────────────────────────────────────────────────────── */

/** Kichik chiziq grafik — toza SVG, tashqi kutubxonasiz. Rang `currentColor` dan olinadi. */
export const Sparkline = forwardRef(function Sparkline(
  { data = [], width = 64, height = 24, strokeWidth = 1.6, className, ...rest },
  ref
) {
  const d = useMemo(() => {
    const pts = data.length >= 2 ? data.map((v) => Number(v) || 0) : [0, 0]
    const min = Math.min(...pts)
    const max = Math.max(...pts)
    const span = max - min || 1
    const pad = 2
    return pts
      .map((v, i) => {
        const x = (i / (pts.length - 1)) * width
        const y = height - ((v - min) / span) * (height - pad * 2) - pad
        return `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(' ')
  }, [data, width, height])

  return (
    <svg
      ref={ref}
      className={cx('ui-sparkline', className)}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
})

/* ─────────────────────────────────────────────────────────────
   KpiStrip
   ───────────────────────────────────────────────────────────── */

/**
 * Seriyadan o'zgarishni hisoblaydi: birinchi yarmi vs ikkinchi yarmi o'rtachasi.
 * Natija KpiStrip ning `delta` propiga to'g'ridan-to'g'ri beriladi.
 *
 * `base` — birinchi yarmining o'rtachasi, ya'ni foiz nimadan hisoblangani.
 * Aynan shu qiymat foiz ko'rsatish yoki ko'rsatmaslikni hal qiladi (pastga qarang).
 *
 * @returns {{pct:number|null, abs:number, base:number}|null}
 */
export function seriesDelta(series) {
  const arr = Array.isArray(series) ? series.map((v) => Number(v) || 0) : []
  if (arr.length < 6) return null
  const half = Math.ceil(arr.length / 2)
  const a = arr.slice(0, half).reduce((x, y) => x + y, 0) / half
  const b = arr.slice(half).reduce((x, y) => x + y, 0) / (arr.length - half)
  return {
    pct: a ? Math.round(((b - a) / a) * 100) : null,
    abs: Math.round((b - a) * 10) / 10,
    base: a,
  }
}

/* Foiz shundan kichik asosda ko'rsatilmaydi.
   Ikkita murojaat uchtaga aylansa "+50%" chiqadi — bu o'sish emas, shovqin:
   raqam katta, ma'nosi yo'q. Kichik asosda absolyut o'zgarish ("+1") ham
   rostroq, ham tezroq o'qiladi. */
const KPI_PCT_MIN_BASE = 10

/* Sparkline shundan past cho'qqida chizilmaydi — 0–2 oralig'idagi seriya
   deyarli tekis chiziq beradi, ya'ni ma'lumot emas, bezak bo'lib qoladi. */
const KPI_SPARK_FLOOR = 3

/** Raqamni yozuvga aylantiradi: 2.0 → "2", 2.4 → "2,4" (o'nlik vergul). */
function fmtDeltaNum(n) {
  const v = Math.abs(n)
  return (Number.isInteger(v) ? String(v) : v.toFixed(1)).replace('.', ',')
}

/** `delta` propini ko'rinadigan matn va yo'nalishga aylantiradi. */
function readDelta(delta) {
  if (delta == null) return null
  const d = typeof delta === 'number' ? { pct: delta } : delta
  const usePct = d.pct != null && (d.base == null || d.base >= KPI_PCT_MIN_BASE)
  // Absolyut ko'rinishda son BUTUNGA yaxlitlanadi: bu yerda gap sanaladigan
  // narsalar haqida ("+1 murojaat"), "+1,3 murojaat" degan narsa yo'q.
  // Nolga yaxlitlansa — aytadigan o'zgarish ham yo'q, delta umuman chiqmaydi.
  const n = usePct ? d.pct : Math.round(d.abs)
  if (n == null || Number.isNaN(n)) return null
  if (!usePct && n === 0) return null
  const dir = n > 0 ? 'up' : n < 0 ? 'down' : 'flat'
  const sign = n > 0 ? '+' : n < 0 ? '−' : ''
  return { dir, text: `${sign}${fmtDeltaNum(n)}${usePct ? '%' : ''}` }
}

/** Qutb (`polarity`) va yo'nalishdan rangni chiqaradi. */
function deltaTone(polarity, dir) {
  if (dir === 'flat') return null
  if (polarity === 'up-good') return dir === 'up' ? 'is-good' : 'is-bad'
  if (polarity === 'up-bad') return dir === 'up' ? 'is-bad' : 'is-good'
  return null                       // qutbsiz hajm ko'rsatkichi — neytral
}

/** Seriyada ko'rsatadigan narsa bormi (tekis chiziq — shovqin). */
function sparkWorthDrawing(data, floor) {
  if (!Array.isArray(data) || data.length < 4) return false
  const nums = data.map((v) => Number(v) || 0)
  const max = Math.max(...nums)
  return max >= floor && max !== Math.min(...nums)
}

/**
 * KPI qatori.
 *
 * `items[]`:
 *   label, value, icon, onClick, spark   — asosiy
 *   unit      — raqamdan keyingi kichik yozuv ("soat", "★")
 *   note      — raqam ostidagi bir qatorlik izoh (masalan navbatchilik)
 *   tone      — 'critical' | 'warn' | 'ok': RAQAM rangi. Faqat ostonasi bor
 *               ko'rsatkichga beriladi (kritik, SLA), hajmga emas.
 *   polarity  — 'up-good' | 'up-bad': o'sish yaxshimi yoki yomonmi.
 *               Berilmasa delta neytral kulrang qoladi.
 *   delta     — son (foiz), yoki `seriesDelta()` qaytargan obyekt.
 *
 * @param sparkFloor  sparkline chiziladigan eng past cho'qqi (standart 3)
 */
export const KpiStrip = forwardRef(function KpiStrip(
  { items = [], sparkFloor = KPI_SPARK_FLOOR, className, ...rest },
  ref
) {
  return (
    <div ref={ref} className={cx('ui-kpi-strip', className)} {...rest}>
      {items.map((it, i) => {
        const Icon = it.icon
        const Tag = it.onClick ? 'button' : 'div'
        const delta = readDelta(it.delta)
        const tone = delta && deltaTone(it.polarity, delta.dir)
        return (
          <Tag
            key={it.label ?? i}
            className={cx('ui-kpi', it.tone && `ui-kpi--${it.tone}`)}
            {...(it.onClick
              ? { type: 'button', onClick: it.onClick, title: it.actionLabel }
              : {})}
          >
            <div className="ui-kpi__value ui-nums">
              {it.value ?? 0}
              {it.unit && <span className="ui-kpi__unit">{it.unit}</span>}
            </div>
            <div className="ui-kpi__label">
              {Icon && <Icon aria-hidden="true" />}
              {it.label}
            </div>
            {it.note && <div className="ui-kpi__note">{it.note}</div>}
            {delta && (
              <div className={cx('ui-kpi__delta', 'ui-nums', `is-${delta.dir}`, tone)}>
                {delta.dir === 'up' && <HiArrowSmallUp aria-hidden="true" />}
                {delta.dir === 'down' && <HiArrowSmallDown aria-hidden="true" />}
                {delta.text}
              </div>
            )}
            {sparkWorthDrawing(it.spark, sparkFloor) && <Sparkline data={it.spark} />}
          </Tag>
        )
      })}
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   SlaMeter — imzo elementi (DESIGN.md 6-bo'lim)
   ───────────────────────────────────────────────────────────── */

const HOUR_MS = 3600 * 1000

function slaState(msLeft) {
  if (msLeft <= 0) return 'breached'
  if (msLeft < HOUR_MS) return 'urgent'
  if (msLeft <= 4 * HOUR_MS) return 'warn'
  return 'calm'
}

function fmtLeft(msLeft) {
  const total = Math.max(0, Math.floor(msLeft / 1000))
  const days = Math.floor(total / 86400)
  const h = Math.floor((total % 86400) / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n) => String(n).padStart(2, '0')
  const clock = `${pad(h)}:${pad(m)}:${pad(s)}`
  return days > 0 ? `${days}k ${clock}` : clock
}

/**
 * SLA hisoblagichi — konsolning yagona rangli elementi.
 * `deadline` — Date | ISO satr | ms. Sekundiga bir marta yangilanadi,
 * holatga qarab is-calm / is-warn / is-urgent / is-breached.
 * `total` — pilik uchun to'liq oyna (ms), standart 4 soat.
 */
export const SlaMeter = forwardRef(function SlaMeter(
  { deadline, showFuse = true, total = 4 * HOUR_MS, className, ...rest },
  ref
) {
  const endAt = useMemo(() => {
    if (deadline == null) return null
    const t = deadline instanceof Date ? deadline.getTime() : new Date(deadline).getTime()
    return Number.isNaN(t) ? null : t
  }, [deadline])

  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (endAt == null) return undefined
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 1000)
    // komponent o'chirilganda interval tozalanadi
    return () => clearInterval(id)
  }, [endAt])

  if (endAt == null) return null

  const left = endAt - now
  const state = slaState(left)
  const breached = state === 'breached'
  const ratio = breached ? 1 : Math.min(1, Math.max(0, left / Math.max(1, total)))
  const label = breached ? "Muddat o'tdi" : fmtLeft(left)

  return (
    <div
      ref={ref}
      className={cx('ui-sla', `is-${state}`, className)}
      role="timer"
      aria-live="off"
      aria-label={breached ? "SLA muddati o'tdi" : `SLA: ${label} qoldi`}
      {...rest}
    >
      <div className="ui-sla__time ui-mono ui-nums">
        {/* Shoshilinch va muddati o'tgan holatda mayoq: signal faqat rang emas
            (DESIGN.md 8). Matnning o'zi holatni aytib turgani uchun belgi
            bezak (label=false). */}
        {(state === 'urgent' || breached) && (
          <Beacon size="sm" label={false} />
        )}
        {label}
      </div>
      {showFuse && (
        <div className="ui-sla__fuse">
          <div className="ui-sla__fill" style={{ width: `${(ratio * 100).toFixed(1)}%` }} />
        </div>
      )}
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   Timeline
   ───────────────────────────────────────────────────────────── */

/** Voqealar lentasi: {icon,title,meta,time}. */
export const Timeline = forwardRef(function Timeline({ items = [], className, ...rest }, ref) {
  return (
    <ol ref={ref} className={cx('ui-timeline', className)} {...rest}>
      {items.map((it, i) => {
        const Icon = it.icon
        return (
          <li key={i} className="ui-timeline__row">
            <span className="ui-timeline__icon">{Icon && <Icon aria-hidden="true" />}</span>
            <div className="ui-timeline__body">
              <div>{it.title}</div>
              {it.meta && <div className="ui-muted">{it.meta}</div>}
            </div>
            {it.time && <time className="ui-muted ui-nums">{it.time}</time>}
          </li>
        )
      })}
    </ol>
  )
})

/* ─────────────────────────────────────────────────────────────
   DescriptionList
   ───────────────────────────────────────────────────────────── */

/** Kalit–qiymat jadvali: {key, value}. */
export const DescriptionList = forwardRef(function DescriptionList(
  { items = [], className, ...rest },
  ref
) {
  return (
    <dl ref={ref} className={cx('ui-dl', className)} {...rest}>
      {items.map((it, i) => (
        <div key={i} className="ui-dl__row">
          <dt className="ui-dl__key">{it.key}</dt>
          <dd className="ui-dl__val">{it.value}</dd>
        </div>
      ))}
    </dl>
  )
})

/* ─────────────────────────────────────────────────────────────
   Accordion
   ───────────────────────────────────────────────────────────── */

/** Yig'iluvchi bo'limlar. `multiple` bo'lsa bir nechtasi bir vaqtda ochiladi. */
export const Accordion = forwardRef(function Accordion(
  { items = [], multiple = false, defaultOpen, className, ...rest },
  ref
) {
  const uid = useId()
  const [openIds, setOpenIds] = useState(() => {
    if (defaultOpen == null) return []
    return Array.isArray(defaultOpen) ? defaultOpen : [defaultOpen]
  })

  const toggle = (id) => {
    setOpenIds((cur) => {
      const isOpen = cur.includes(id)
      if (multiple) return isOpen ? cur.filter((x) => x !== id) : [...cur, id]
      return isOpen ? [] : [id]
    })
  }

  return (
    <div ref={ref} className={cx('ui-accordion', className)} {...rest}>
      {items.map((it) => {
        const isOpen = openIds.includes(it.id)
        const bodyId = `${uid}-${it.id}`
        return (
          <div key={it.id}>
            <button
              type="button"
              className={cx('ui-accordion__hdr', isOpen && 'is-active')}
              aria-expanded={isOpen}
              aria-controls={bodyId}
              onClick={() => toggle(it.id)}
            >
              <span className="ui-grow">{it.title}</span>
              <HiChevronDown aria-hidden="true" />
            </button>
            <div id={bodyId} className="ui-accordion__body" hidden={!isOpen}>
              {it.body}
            </div>
          </div>
        )
      })}
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   CommandPalette
   ───────────────────────────────────────────────────────────── */

/**
 * Buyruqlar paneli. Ctrl+K / Cmd+K ochadi (`hotkey={false}` bilan o'chiriladi),
 * yozib qidiriladi, strelkalar bilan yuriladi, Enter tanlaydi, Esc yopadi.
 * `groups`: {title, items:[{id,label,icon,onSelect,hint}]}
 */
export const CommandPalette = forwardRef(function CommandPalette(
  { open, onClose, onOpen, groups = [], hotkey = true, placeholder = 'Buyruq yoki sahifa…', className, ...rest },
  ref
) {
  const panelRef = useRef(null)
  const listRef = useRef(null)
  const [q, setQ] = useState('')
  const [cursor, setCursor] = useState(0)
  const uid = useId()

  const openRef = useRef(open)
  const onOpenRef = useRef(onOpen)
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    openRef.current = open
    onOpenRef.current = onOpen
    onCloseRef.current = onClose
  })

  useDialog(open, onClose, panelRef)

  // Ctrl+K / Cmd+K
  useEffect(() => {
    if (!hotkey || typeof window === 'undefined') return undefined
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        if (openRef.current) onCloseRef.current?.()
        else onOpenRef.current?.()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [hotkey])

  useEffect(() => {
    if (open) {
      setQ('')
      setCursor(0)
    }
  }, [open])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return groups
      .map((g) => ({
        ...g,
        items: (g.items || []).filter(
          (it) => !needle || String(it.label ?? '').toLowerCase().includes(needle)
        ),
      }))
      .filter((g) => g.items.length > 0)
  }, [groups, q])

  const flat = useMemo(() => filtered.flatMap((g) => g.items), [filtered])

  useEffect(() => {
    setCursor((c) => (flat.length === 0 ? 0 : Math.min(c, flat.length - 1)))
  }, [flat.length])

  // tanlangan qatorni ko'rinishda ushlab turish
  useEffect(() => {
    if (!open) return
    const node = listRef.current?.querySelector(`[data-cmdk-index="${cursor}"]`)
    node?.scrollIntoView({ block: 'nearest' })
  }, [cursor, open])

  const select = (item) => {
    onClose?.()
    item?.onSelect?.()
  }

  const onKeyDown = (e) => {
    if (!flat.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => (c + 1) % flat.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => (c - 1 + flat.length) % flat.length)
    } else if (e.key === 'Home') {
      e.preventDefault()
      setCursor(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setCursor(flat.length - 1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      select(flat[cursor])
    }
  }

  if (!open) return null

  let index = -1

  return (
    <Portal>
      <div
        className="ui-modal-overlay"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.()
        }}
      >
        <div
          ref={(node) => {
            panelRef.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) ref.current = node
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Buyruqlar paneli"
          tabIndex={-1}
          className={cx('ui-cmdk', className)}
          onKeyDown={onKeyDown}
          {...rest}
        >
          <div className="ui-row ui-gap-8">
            <HiMagnifyingGlass aria-hidden="true" />
            <input
              className="ui-cmdk__input ui-grow"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              aria-label="Buyruq qidirish"
              aria-controls={`${uid}-list`}
              aria-activedescendant={flat.length ? `${uid}-opt-${cursor}` : undefined}
              autoComplete="off"
            />
            <Kbd>Esc</Kbd>
          </div>

          <div ref={listRef} id={`${uid}-list`} role="listbox" aria-label="Natijalar">
            {filtered.length === 0 && <EmptyState title="Natija topilmadi" icon={HiMagnifyingGlass} />}
            {filtered.map((g) => (
              <div key={g.title ?? 'group'}>
                {g.title && <div className="ui-cmdk__group">{g.title}</div>}
                {g.items.map((it) => {
                  index += 1
                  const i = index
                  const Icon = it.icon
                  return (
                    <div
                      key={it.id ?? i}
                      id={`${uid}-opt-${i}`}
                      data-cmdk-index={i}
                      role="option"
                      aria-selected={i === cursor}
                      className={cx('ui-cmdk__item', i === cursor && 'is-active')}
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => select(it)}
                    >
                      {Icon && <Icon aria-hidden="true" />}
                      <span className="ui-grow ui-truncate">{it.label}</span>
                      {it.hint && <span className="ui-muted">{it.hint}</span>}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Portal>
  )
})

/* ─────────────────────────────────────────────────────────────
   TabBar — pastki navigatsiya (telefon uchun)
   ───────────────────────────────────────────────────────────── */

/**
 * Ekran pastida suzadigan shisha kapsula: kunlik ish yuzalari + o'ng tomonda
 * ajratilgan bitta doira.
 *
 * Nima uchun `Tabs` emas: `Tabs` — bitta karta ICHIDAGI ko'rinishlarni
 * almashtiradi, bu esa ilovaning YUZALARI orasida yuradi. Ikkisi bir joyda
 * ishlatilsa foydalanuvchi qaysi biri sahifani almashtirishini bilmay qoladi,
 * shuning uchun ular ataylab alohida komponent.
 *
 * Yuza soni 3 dan 5 gacha. Ko'proq bo'lsa yorliqlar o'qilmay qoladi va tanlash
 * "qidirish"ga aylanadi — qolganlari yon panelda qoladi.
 *
 * @param items  [{ id, label, icon, count, tone }] — `tone: 'critical'` faqat
 *               hisoblagichni bo'yaydi, yorliqni emas.
 * @param value  faol yuzaning `id` si
 * @param aside  { label, onClick, count, tone, content } — kapsuladan tashqaridagi
 *               doira. `content` — ichidagi tugun (ikonka, `Beacon` yoki `Loader`).
 *               Berilmasa doira umuman chizilmaydi.
 */
export const TabBar = forwardRef(function TabBar(
  { items = [], value, onChange, aside, label = 'Asosiy yuzalar', className, ...rest },
  ref
) {
  const activeIndex = Math.max(0, items.findIndex((it) => it.id === value))

  return (
    <div ref={ref} className={cx('ui-tabbar', className)} {...rest}>
      <nav
        className="ui-tabbar__nav"
        aria-label={label}
        style={{ '--ui-tabbar-n': items.length || 1, '--ui-tabbar-i': activeIndex }}
      >
        {/* Siyoh tomchisi ro'yxatdan OLDIN: u fon, tugmalar esa ustida */}
        <span className="ui-tabbar__ind" aria-hidden="true" />

        {items.map((it) => {
          const active = it.id === value
          const Icon = it.icon
          return (
            <button
              key={it.id}
              type="button"
              aria-current={active ? 'page' : undefined}
              className={cx('ui-tabbar__item', active && 'is-active')}
              onClick={() => onChange?.(it.id)}
            >
              {Icon && <Icon className="ui-tabbar__icon" aria-hidden="true" />}
              {it.count > 0 && (
                <span className={cx('ui-tabbar__badge', it.tone === 'critical' && 'ui-tabbar__badge--critical')}>
                  {it.count > 9 ? '9+' : it.count}
                </span>
              )}
              <span className="ui-tabbar__lbl">{it.label}</span>
              {/* Raqam ko'zga ko'rinadi, lekin "9+" ekran o'quvchiga hech narsa
                  demaydi — aniq soni faqat shu yerda aytiladi. */}
              {it.count > 0 && <span className="ui-sr-only">, {it.count} ta</span>}
            </button>
          )
        })}
      </nav>

      {aside && (
        <button
          type="button"
          className="ui-tabbar__aside"
          aria-label={aside.count > 0 ? `${aside.label}: ${aside.count} ta` : aside.label}
          onClick={aside.onClick}
        >
          {aside.content}
          {aside.count > 0 && (
            <span className={cx('ui-tabbar__badge', aside.tone === 'critical' && 'ui-tabbar__badge--critical')}>
              {aside.count > 9 ? '9+' : aside.count}
            </span>
          )}
        </button>
      )}
    </div>
  )
})
